import type { EditRequest } from "@loumy/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type SubmitJobInput } from "../api/client";
import { useSession } from "../auth/session";
import { qk } from "./queryClient";

// Datenhaken (§4) — alle gegen die interne API (D47). Nur aktiv, wenn angemeldet;
// abgemeldet bleiben die Listen leer (definierter Leerzustand, kein Fehlerflackern).

export function useAuthedEnabled(): boolean {
  return useSession().status === "signed-in";
}

export function useFolders() {
  const enabled = useAuthedEnabled();
  // D149: cache-first — ein warmer Reload nutzt den persistierten Cache (kein Spinner), Freshness
  // kommt über den Delta-Sync (useLiveStatus). Mutationen invalidieren weiterhin gezielt (refetch).
  return useQuery({ queryKey: qk.folders, queryFn: api.listFolders, enabled, staleTime: Infinity, refetchOnMount: false });
}

export function useAssets(folderId: string | null) {
  const enabled = useAuthedEnabled();
  return useQuery({ queryKey: qk.assets(folderId), queryFn: () => api.listAssets(folderId), enabled });
}

export function useJobs() {
  const enabled = useAuthedEnabled();
  // REALTIME-ONLY (D47): KEIN Polling. Cold-Mount fetcht einmal; danach kommen Updates über den
  // Delta-Sync (D149: useLiveStatus mergt geänderte Jobs in den Cache, kein Voll-Refetch). Ein
  // warmer Reload nutzt den persistierten Cache (staleTime Infinity, kein refetchOnMount) → kein
  // Spinner. Ohne erreichbare DO bleibt die Liste beim letzten Stand — bewusst, statt zu pollen.
  return useQuery({ queryKey: qk.jobs, queryFn: api.listJobs, enabled, staleTime: Infinity, refetchOnMount: false });
}

// D155: presignte Vorschau-Bilder je Job (Eingabe-Bild Vorrang, Output-Fallback) für die Jobs-Liste.
// BEWUSST NICHT cache-first wie useJobs: presigned URLs sind kurzlebig (D56) — der QueryClient-
// Default (staleTime 15s, refetchOnMount) zieht beim Mount frische URLs, statt eine abgelaufene aus
// dem persistierten Cache zu servieren. Aktualität bei Job-Übergängen: deltaSync invalidiert qk.jobPreviews,
// sobald sich Jobs geändert haben (ein FERTIG gewordener Job bekommt so sein Thumbnail). Nicht
// persistiert (queryPersist), weil abgelaufene URLs nach einem Reload wertlos wären.
export function useJobPreviews() {
  const enabled = useAuthedEnabled();
  return useQuery({ queryKey: qk.jobPreviews, queryFn: api.listJobPreviews, enabled });
}

export function useCredits() {
  const enabled = useAuthedEnabled();
  // D149: cache-first; das Saldo kommt über den Delta-Sync mit (jeder Delta trägt `credits`) +
  // gezielte Invalidierung nach Submit/Redeem.
  return useQuery({ queryKey: qk.credits, queryFn: api.credits, enabled, staleTime: Infinity, refetchOnMount: false });
}

// Job-Detail (§11/D65) — Job + Inputs + Outputs (mit presigned downloadUrls) für das „Job öffnen".
// Nur aktiv, wenn ein Job geöffnet ist; Updates kommen über den Live-Push (useLiveStatus
// invalidiert qk.job(*) mit), sodass das Ergebnis erscheint, sobald der Job fertig ist.
export function useJobDetail(jobId: string | null) {
  const enabled = useAuthedEnabled() && jobId != null;
  return useQuery({
    queryKey: qk.job(jobId ?? ""),
    queryFn: () => api.getJob(jobId as string),
    enabled,
  });
}

export function useSubmitJob() {
  const client = useQueryClient();
  return useMutation({
    // §11/D161 (amendiert D143): Outputs landen IMMER im tool-Default-Ordner ("<Tool>/Inbox",
    // autoFolder) — UNABHÄNGIG davon, welcher Ordner gerade gewählt ist. Der gewählte Ordner ist
    // NICHT mehr das Submit-Ziel (das verteilte Jobs sonst nach Auswahl), sondern nur noch das Ziel
    // des per-Job-„In <Ordner> verschieben"-Buttons (JobsList). Daher kein selectedFolderId-Default
    // mehr; ein explizit übergebener `targetFolderId` (Public-API/Sonderpfad) gewinnt weiterhin.
    mutationFn: (input: SubmitJobInput) =>
      api.submitJob({
        ...input,
        targetFolderId: input.targetFolderId ?? null,
        autoFolder: input.autoFolder ?? true,
      }),
    onSuccess: () => {
      // Job erzeugt → Jobs-Liste + Credits frisch ziehen (Hold gebucht, D34/D36).
      void client.invalidateQueries({ queryKey: qk.jobs });
      void client.invalidateQueries({ queryKey: qk.credits });
      // D143: evtl. wurde ein tool-Default-Ordner NEU angelegt → Folder-Tree frisch ziehen.
      void client.invalidateQueries({ queryKey: qk.folders });
    },
  });
}

// Browser-Edit speichern (§9.17/D124): das im Browser berechnete PNG wird materialisiert +
// abgebucht → Asset-Listen UND Credits invalidieren (direkter Charge, kein Hold).
export function useSubmitEdit() {
  const client = useQueryClient();
  return useMutation({
    // signal (D130-Codex-P1): optionaler Abort bis in den fetch — Schließen mid-Upload
    // bricht den POST ab, statt still zu materialisieren+chargen.
    mutationFn: ({ png, meta, signal }: { png: Uint8Array; meta: EditRequest; signal?: AbortSignal }) =>
      api.submitEdit(png, meta, signal),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["assets"] });
      void client.invalidateQueries({ queryKey: qk.credits });
    },
  });
}

export function useUploadAsset() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, folderId }: { file: File; folderId: string | null }) => api.uploadAsset(file, folderId),
    onSuccess: (_asset, vars) => {
      // Neues Upload-Asset → die Asset-Liste des Ziel-Ordners frisch ziehen (§9.4).
      void client.invalidateQueries({ queryKey: qk.assets(vars.folderId) });
    },
  });
}

// Folder-/Asset-Mutationen (§9.1/§11/D100). Invalidierung: Folder-Mutationen → qk.folders;
// Asset-Move + Folder-Trash zusätzlich ALLE Asset-Listen (Prefix ["assets"] trifft Quelle UND
// Ziel — beim Trash verschwinden Assets aus jedem Subtree-Ordner, D20).

export function useCreateFolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: string | null }) => api.createFolder(name, parentId),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useRenameFolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameFolder(id, name),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useMoveFolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, parentId }: { id: string; parentId: string | null }) => api.moveFolder(id, parentId),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.folders }),
  });
}

export function useTrashFolder() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.trashFolder(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.folders });
      void client.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useMoveAsset() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) => api.moveAsset(id, folderId),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["assets"] }),
  });
}

export function useMoveJobOutputs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) => api.moveJobOutputs(id, folderId),
    onSuccess: () => void client.invalidateQueries({ queryKey: ["assets"] }),
  });
}

export function useRedeemCode() {
  const client = useQueryClient();
  return useMutation({
    // Frischer Idempotency-Key pro Versuch (§9.15, user-scoped) → kein Doppel-Einlösen bei Retry.
    mutationFn: (code: string) => api.redeemCode(code, crypto.randomUUID()),
    onSuccess: (res) => {
      // Endpoint liefert das neue Saldo → ohne Extra-Roundtrip in den Cache schreiben.
      client.setQueryData(qk.credits, res.available);
    },
  });
}

// API-Keys (§9.16 Account-Surface): Liste + Mint (Klartext nur in der Mutations-Response,
// NICHT im Query-Cache) + Widerruf. BEWUSST kein useCreateApiKey-Hook: die Mint-Response
// enthält den KLARTEXT, der nie im TanStack-MutationCache landen darf (Codex-P1, Devtools-/
// Persistenz-Risiko) — die Account-Seite ruft api.createApiKey direkt und hält den Klartext
// nur im lokalen Component-State der Einmal-Anzeige.
export function useApiKeys() {
  const enabled = useAuthedEnabled();
  return useQuery({ queryKey: qk.apiKeys, queryFn: api.listApiKeys, enabled });
}

export function useRevokeApiKey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: () => void client.invalidateQueries({ queryKey: qk.apiKeys }),
  });
}
